import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMarket } from "./MarketProvider";
import { splitMarketPath, withMarket } from "./markets";
import { useV3 } from "./useV3";
import { ChevronDown, Flag } from "./Icons";

/* Switching market changes the URL prefix and keeps the rest of the path, so
 * /eg/search?for=Rent becomes /sa/search?for=Rent. The visitor stays where
 * they were and the page refetches for the new market -- they do not get
 * thrown back to a homepage for having changed country.
 *
 * It is a listbox, opened on click, closed on Escape or an outside click, and
 * it moves focus back to the button on close. A `<select>` would have been
 * less code and the design draws flags in the options, which no `<select>`
 * can carry on every platform.
 */
export function MarketSwitcher({ compact = false }: { compact?: boolean }) {
  const { market, markets } = useMarket();
  const { s } = useV3();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); button.current?.focus(); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // One market is not a choice. The design draws the control because two
  // markets exist; with one it would be a dropdown that can only pick what is
  // already picked.
  if (!market || markets.length < 2) return null;

  const go = (slug: string) => {
    const { rest } = splitMarketPath(location.pathname);
    setOpen(false);
    navigate(withMarket(rest, slug) + location.search);
  };

  return (
    <div ref={wrap} className="sq-market-switcher-wrap">
      <button
        ref={button}
        type="button"
        data-component="market-switcher"
        className={`sq-market-switcher${compact ? " sq-market-switcher--compact" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={compact ? market.nameAr : s.marketSwitcher.label}
        onClick={() => setOpen((o) => !o)}
      >
        <Flag code={market.code} />
        {!compact && <span><MarketName market={market} /></span>}
        <ChevronDown />
      </button>
      {open && (
        <ul className="sq-market-switcher__menu" role="listbox" aria-label={s.marketSwitcher.label}>
          {markets.map((m) => (
            <li key={m.id} role="option" aria-selected={m.id === market.id}>
              {/* A closed market is still reachable -- the design draws the
                  door and /sa/ is a real URL -- but it says what is behind it
                  before the click, not after. Disabling it would hide a market
                  we are actively preparing. */}
              <button type="button" onClick={() => go(m.slug)}>
                <Flag code={m.code} />
                <MarketName market={m} />
                {m.visibility === "coming_soon" && <small className="sq-market-tag">{s.marketComingSoon.tag}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* A market's name is on its row in both scripts. The active locale picks
 * which, so «مصر» in Arabic and "Egypt" in English, and neither is written
 * in the client. */
export function MarketName({ market }: { market: { nameAr: string; nameEn: string } }) {
  const { ar } = useV3();
  return <>{ar ? market.nameAr : market.nameEn}</>;
}
